import { Component, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import {
  HebcalService,
  ShabbatData,
} from "../print-balance-table/services/getHebrewDateService";

// html2canvas-pro and jspdf are dynamically imported inside buildPdfBlob()
// so they don't inflate the app's initial bundle (~700KB saved).

@Component({
  selector: "app-print-aliyot",
  templateUrl: "./print-aliyot.component.html",
  styleUrl: "./print-aliyot.component.scss",
})
export class PrintAliyotComponent implements OnInit {
  shabbatData: ShabbatData | null = null;

  hazanIcon = "assets/jewish.png";
  candelsIcon = "assets/sabbath.png";
  bookIcon = "assets/torah.png";
  talitIcon = "assets/tallit.png";
  davidIcon = "assets/star-of-david.png";
  shofarIcon = "assets/shofar.png";
  haftaraIcon = "assets/haftara.png";

  ptihatHihal: string = "";
  /** Haftara text shown on the print. Defaults to the parasha name. */
  haftara: string = "";
  errorMessage: string = "";
  selectedParasha: string | null = null;
  selectedYear: string | null = null;
  selectedBook: string | null = null;

  // Store manual data from URL
  manualOrders: any[] = [];
  manualPtihatHihal: string | null = null;
  /** Optional manual haftara name. Empty → falls back to parasha. */
  manualHaftara: string | null = null;
  /** Per-position hazanim override. Empty slot → keep server's. */
  manualHazanim: string[] = [];

  constructor(
    private hebcalService: HebcalService,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      const parasha = params["parasha"] || "latest";
      const year = params["year"] || new Date().getFullYear().toString();
      const book = params["book"] || null;

      // Capture manual inputs from URL
      this.manualPtihatHihal = params["ptihatHihal"] || null;
      this.manualHaftara = params["haftara"] || null;
      if (params["orders"]) {
        try {
          this.manualOrders = JSON.parse(params["orders"]);
        } catch (e) {
          console.error("Parse orders error", e);
        }
      }
      if (params["hazanim"]) {
        try {
          const parsed = JSON.parse(params["hazanim"]);
          this.manualHazanim = Array.isArray(parsed) ? parsed : [];
        } catch (e) {
          console.error("Parse hazanim error", e);
        }
      }

      this.selectedParasha = parasha === "latest" ? null : parasha;
      this.selectedYear = year;
      this.selectedBook = book;

      if (parasha !== "latest") {
        this.getAliyotByParasha(parasha, year);
      } else {
        this.getHebrewDate();
      }
    });
  }

  private getHebrewDate(): void {
    this.hebcalService
      .getHebrewDate()
      .then((data) => {
        this.shabbatData = data;
        this.applyManualOverrides(); // Trigger override here
      })
      .catch((error) => {
        this.errorMessage = "שגיאה בטעינת הנתונים";
      });
  }

  private getAliyotByParasha(parasha: string, year?: string): void {
    this.hebcalService
      .getAliyotByParasha(parasha, year)
      .then((data) => {
        if ("msg" in data) {
          this.errorMessage = data.msg;
          return;
        }
        this.shabbatData = data;
        this.applyManualOverrides(); // Trigger override here
      })
      .catch((err) => console.error(err));
  }

  private applyManualOverrides(): void {
    if (!this.shabbatData) return;

    // 1. Override Book name
    if (this.selectedBook) {
      this.shabbatData.book = this.selectedBook;
    }

    // 2. Override Aliyot Names
    if (this.manualOrders && this.manualOrders.length > 0 && this.shabbatData.orders) {
      // Loop through the data coming FROM THE API/SERVICE
      this.shabbatData.orders.forEach(apiOrder => {
        // Find if we have a manual name for this specific Aliya
        const manualMatch = this.manualOrders.find(m => m.name === apiOrder.name);
        if (manualMatch && manualMatch.userName.trim() !== '') {
          apiOrder.userName = manualMatch.userName;
        }
      });
    }

    // 3. Override Hazanim — "replace-all when any filled" semantics.
    //    If the admin entered ANY manual hazan, the print shows ONLY the
    //    filled names (in slot order, empty slots skipped). The full
    //    server default is dropped — so entering one name produces a
    //    single-hazan print rather than that name + 2 server defaults.
    //    All slots empty → server's defaults are kept untouched.
    const filledHazanim = this.manualHazanim
      .map((h) => (h ?? '').trim())
      .filter((h) => h !== '');
    if (filledHazanim.length > 0 && Array.isArray(this.shabbatData.hazan)) {
      this.shabbatData.hazan = filledHazanim;
    }

    // 4. Process Ptihat Heihal display
    this.processPtihatHihal();

    // 5. Compute the haftara display value: manual override if filled,
    //    else fall back to the parasha name (existing behaviour).
    this.haftara =
      this.manualHaftara && this.manualHaftara.trim() !== ''
        ? this.manualHaftara.trim()
        : this.shabbatData.parasha ?? '';
  }

  private processPtihatHihal(): void {
    // If we have a manual input, use it. Otherwise, look in the orders array.
    if (this.manualPtihatHihal && this.manualPtihatHihal.trim() !== '') {
      this.ptihatHihal = this.manualPtihatHihal;
    } else if (this.shabbatData?.orders) {
      const matchingOrder = this.shabbatData.orders.find((order) =>
        order.name.includes("פתיחת היכל")
      );
      this.ptihatHihal = matchingOrder ? matchingOrder.userName : "לא נמצא";
    } else {
      this.ptihatHihal = "לא נמצא";
    }
  }

  // ── PDF generation flag — drives the button spinner ────────────────
  pdfBusy = false;

  /**
   * True only if the browser truly supports sharing a File via the Web
   * Share API. Desktop browsers usually return false → the WhatsApp
   * button is hidden there (you'd download the PDF and attach manually
   * inside WhatsApp Web yourself, which is fine).
   *
   * Probed once with a tiny dummy PDF File; the result is stable for
   * the page lifetime.
   */
  readonly canShareFiles: boolean = (() => {
    try {
      const nav = window.navigator as Navigator & {
        canShare?: (data: { files: File[] }) => boolean;
        share?: (data: ShareData & { files?: File[] }) => Promise<void>;
      };
      if (typeof nav.share !== 'function' || typeof nav.canShare !== 'function') {
        return false;
      }
      const probe = new File([new Uint8Array([0x25, 0x50, 0x44, 0x46])], 'probe.pdf', {
        type: 'application/pdf',
      });
      return nav.canShare({ files: [probe] });
    } catch {
      return false;
    }
  })();

  onPrint(): void {
    window.print();
  }

  /** Download the aliyot sheet as a PDF straight to disk. */
  async downloadPdf(): Promise<void> {
    if (this.pdfBusy) return;
    this.pdfBusy = true;
    try {
      const blob = await this.buildPdfBlob();
      this.triggerDownload(blob, this.makeFileName());
    } catch (err) {
      console.error('Download PDF failed:', err);
      alert('שגיאה ביצירת הקובץ');
    } finally {
      this.pdfBusy = false;
    }
  }

  /**
   * Generate the PDF and share it as a File via the native Web Share
   * API. The button calling this method is only rendered when
   * `canShareFiles` is true, so this path is guaranteed to work —
   * no desktop fallback that asks the user to manually attach.
   */
  async shareViaWhatsApp(): Promise<void> {
    if (this.pdfBusy || !this.canShareFiles) return;
    this.pdfBusy = true;
    try {
      const blob = await this.buildPdfBlob();
      const filename = this.makeFileName();
      const file = new File([blob], filename, { type: 'application/pdf' });

      const nav = window.navigator as Navigator & {
        share: (data: ShareData & { files?: File[] }) => Promise<void>;
      };

      try {
        await nav.share({
          title: this.shareTitle(),
          text: this.shareCaption(),
          files: [file],
        });
      } catch (err) {
        // AbortError = user cancelled the share sheet; ignore quietly.
        if ((err as { name?: string })?.name !== 'AbortError') {
          console.error('navigator.share failed:', err);
          alert('שיתוף הקובץ נכשל');
        }
      }
    } catch (err) {
      console.error('Share PDF failed:', err);
      alert('שגיאה ביצירת הקובץ לשיתוף');
    } finally {
      this.pdfBusy = false;
    }
  }

  // ── PDF helpers ────────────────────────────────────────────────────

  /**
   * Capture `.aliyot-page` to a canvas and wrap it in an A4 portrait PDF.
   *
   * Uses html2canvas-pro (handles modern `oklch()` / `color-mix()` color
   * functions that Tailwind + daisyUI emit — vanilla html2canvas throws
   * "unsupported color function 'oklch'" on those). Then jsPDF puts the
   * resulting JPEG onto a single A4 page with a 6mm safe-zone margin.
   */
  private async buildPdfBlob(): Promise<Blob> {
    const element = document.querySelector('.aliyot-page') as HTMLElement | null;
    if (!element) throw new Error('aliyot page element not found');

    // Apply a "pdf export" class so the printed-looking styles are active
    // during capture (html2canvas does not honour @media print).
    document.body.classList.add('pdf-export-mode');
    await new Promise(requestAnimationFrame);
    await new Promise(requestAnimationFrame);

    // Lazy-load the heavy PDF stack only when the user actually asks for it.
    const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
      import('html2canvas-pro'),
      import('jspdf'),
    ]);

    try {
      const canvas = await html2canvas(element, {
        scale: 3,                      // higher DPI for crisp Hebrew text
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
        // Skip any element marked `.noPrint` (the action toolbar) so it
        // doesn't end up in the PDF capture. Cheaper than display:none
        // because the element doesn't occupy whitespace either.
        ignoreElements: (el: Element) => {
          if (!(el instanceof HTMLElement)) return false;
          return el.classList?.contains('noPrint') ?? false;
        },
      });

      const imgData = canvas.toDataURL('image/jpeg', 0.97);

      const pdf = new jsPDF({
        unit: 'mm',
        format: 'a4',
        orientation: 'portrait',
      });

      // A4 portrait: 210 × 297 mm. Reserve a 6mm safe margin all around.
      const pageW = 210;
      const pageH = 297;
      const margin = 6;
      const availW = pageW - 2 * margin;
      const availH = pageH - 2 * margin;

      // Preserve aspect ratio, scale to fit within the safe area.
      const ratio = canvas.height / canvas.width;
      let renderW = availW;
      let renderH = renderW * ratio;
      if (renderH > availH) {
        renderH = availH;
        renderW = renderH / ratio;
      }
      // Horizontal: center. Vertical: TOP-align (was centered, which
      // left a large empty strip above wider-than-tall sheets). Any
      // spare vertical space now falls at the bottom.
      const offsetX = (pageW - renderW) / 2;
      const offsetY = margin;

      pdf.addImage(imgData, 'JPEG', offsetX, offsetY, renderW, renderH);

      return pdf.output('blob');
    } finally {
      document.body.classList.remove('pdf-export-mode');
    }
  }

  private triggerDownload(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // Give the download a moment to start before revoking.
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  }

  private makeFileName(): string {
    const parasha = (this.shabbatData?.parasha || 'shabbat').replace(/[\\/:*?"<>|]/g, '');
    // Prefer the Shabbat date this sheet was generated for; fall back to
    // today's date if the API hasn't returned a candle-lighting time yet.
    const date = this.pdfDatePrefix(this.shabbatData?.shabbatStart);
    return `${date}_${parasha}_aliyot.pdf`;
  }

  /** YYYY-MM-DD prefix used in exported filenames. Sortable + unambiguous. */
  private pdfDatePrefix(iso?: string): string {
    let d: Date;
    try {
      d = iso ? new Date(iso) : new Date();
      if (Number.isNaN(d.getTime())) d = new Date();
    } catch {
      d = new Date();
    }
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  private shareTitle(): string {
    const parasha = this.shabbatData?.parasha ?? '';
    return parasha ? `טופס עליות שבת ${parasha}` : 'טופס עליות שבת קודש';
  }

  private shareCaption(): string {
    const formatHHmm = (iso?: string): string => {
      if (!iso) return '';
      try {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return '';
        return d.toLocaleTimeString('he-IL', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
          timeZone: 'Asia/Jerusalem',
        });
      } catch {
        return '';
      }
    };
    const parasha = this.shabbatData?.parasha || '';
    const start = formatHHmm(this.shabbatData?.shabbatStart);
    const end = formatHHmm(this.shabbatData?.shabbatEnd);
    const book = this.shabbatData?.book || '';

    const lines = [
      `📜 טופס עליות שבת קודש`,
      parasha ? `פרשת ${parasha}` : '',
      this.shabbatData?.hebrew || '',
      '',
      start ? `🕯 כניסת שבת: ${start}` : '',
      end ? `🕯 צאת שבת: ${end}` : '',
      book ? `📖 ${book}` : '',
      '',
      `שבת שלום ומבורך 🙏`,
      `בית הכנסת תפארת ישראל, גני צבי`,
    ].filter((l) => l !== undefined);
    return lines.join('\n');
  }
}